import { ReactNode, useMemo, useRef } from "react";
import { useTrackingOp } from "../../../../contexts/TrackingOpContext";
import InfoFish from "../../../../pages/PlanOperationPage/Popup/EditOp/_components/InfoFish";
import { datetime } from "../../../../util/library/datetime";
import { EXECUTION_TYPE, OP_RUNNING_MODE, STEP_KEY_V2 } from "../../../../util/varibles/constants";
import { IActivityLog, IHarvest2 } from "../../../../util/varibles/interface";
import Density from "../../../Operation/Density2";
import styles from "../style.module.scss";

type THarvestingTask = NonNullable<IActivityLog["real_sub_steps"]>[number];

interface IProps {
    infoByTank?: Record<string, ReactNode>
    remainingByTank?: Record<string, { total_weight: number, fish_amount: number }>
    showLoading?: boolean
    showZero?: boolean
    unitId?: string
}

export default function Tanks({ infoByTank = {}, remainingByTank, showLoading = false, showZero = false, unitId }: IProps) {
    const [op] = useTrackingOp(state => state.op?.operation as IHarvest2);
    const [activity_log] = useTrackingOp(state => state.op?.activity_log);

    const tanks = useRef((() => {
        const { tanks = [] } = op || {};
        return [...tanks].sort((a, b) => a.name.localeCompare(b.name));
    })());

    const { densityByTank, group, taskByTank } = useMemo(() => {
        if (remainingByTank)
            return { densityByTank: remainingByTank, group: remainingByTank, taskByTank: {} };

        const { real_sub_steps = [] } = (activity_log || []).find(item => item.key === STEP_KEY_V2.HARVESTING) || {};
        const densityByTank = real_sub_steps.reduce((rs: {
            [tankId: string]: { total_weight: number, fish_amount: number }
        }, item) => {
            if (!item.real_finish_time) {
                return rs;
            }

            const { tank_id, total_weight = 0, fish_amount = 0 } = item;
            if (rs[tank_id]) {
                rs[tank_id].total_weight += total_weight;
                rs[tank_id].fish_amount += fish_amount;
            } else {
                rs[tank_id] = { total_weight, fish_amount };
            }

            return rs;
        }, {});
        const filteredSteps = unitId
            ? real_sub_steps.filter(item => item.unit_id === unitId)
            : real_sub_steps;

        const result = filteredSteps.reduce((rs: {
            group: { [tankId: string]: { total_weight: number, fish_amount: number } }
            taskByTank: { [tankId: string]: THarvestingTask }
        }, item) => {
            const { tank_id, total_weight = 0, fish_amount = 0 } = item;
            if (rs.group[tank_id]) {
                rs.group[tank_id].total_weight += total_weight;
                rs.group[tank_id].fish_amount += fish_amount;
            } else {
                rs.group[tank_id] = { total_weight, fish_amount };
            }
            rs.taskByTank[tank_id] = item;

            return rs;
        }, { group: {}, taskByTank: {} })

        return { ...result, densityByTank };
    }, [activity_log, remainingByTank, unitId])

    if (op?.op_running_mode !== OP_RUNNING_MODE.DETAIL)
        return null;

    return <div className={styles.tanks} style={{ '--tank-count': tanks.current.length } as any}>
        {tanks.current.map(item => {
            const { total_weight = 0, fish_amount = 0 } = group[item.id] || {};
            const densityFishAmount = densityByTank[item.id]?.fish_amount || 0;
            const densityTotalWeight = densityByTank[item.id]?.total_weight || 0;
            const task = taskByTank[item.id];
            const tankDensity = op.execution_type === EXECUTION_TYPE.AUTO && op.op_running_mode === OP_RUNNING_MODE.DETAIL
                ? item.real_density || 0
                : item.density || 0;
            const density = (densityTotalWeight > 0 || !!remainingByTank) && item.total_weight
                ? tankDensity * densityTotalWeight / item.total_weight
                : tankDensity;

            return <div key={item.id}>
                <div className={styles.tank} >
                    <div className="flex justify-end">
                        {showLoading && task?.real_start_time && <div className="flex gap-1 text-[14px] leading-[23px]">
                            <span className="text-comment">{datetime(task.real_start_time).format("HH:mm")}</span>
                            <span>Loading</span>
                            <span className="text-comment">{task.real_finish_time ? datetime(task.real_finish_time).format("HH:mm") : "_ _"}</span>
                        </div>}
                    </div>
                    <div className="flex !justify-center" data-lev="in-tank" >
                        <InfoFish data={{ fish_amount, total_weight }} showZero={showZero} />
                    </div>
                    <div className="!justify-between pr-3" data-lev="tank-name">
                        <div className="text-comment font-w5 text-[14px]">{item.name}</div>
                        {(densityFishAmount > 0 || densityTotalWeight > 0) &&
                            <Density density={density} max_density={item.max_density} />}
                    </div>
                </div>
                {infoByTank[item.id]}
            </div>
        })}
    </div>
}
